import { Box, Typography, TextField, InputAdornment, FormControl, InputLabel, Select } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import FilterTabs from "./FilterTabs";

/**
 * PageToolbar — a single, self-contained control strip that combines the
 * type filters, search, sort, result count, and an optional action button
 * into one cohesive line. Used on the Datasets and Learning Models pages to
 * give the header a distinctive, app-like toolbar instead of stacked rows.
 *
 * Layout (one line, wraps gracefully on narrow widths):
 *   [ filter tabs ]  ·  count            [ search ] [ sort ] [ action ]
 */
const PageToolbar = ({
  tabs = [],
  filterValue,
  onFilterChange,
  searchQuery,
  setSearchQuery,
  searchPlaceholder = "Search...",
  sortBy,
  setSortBy,
  sortOptions,
  countText,
  rightExtra,
}) => {
  return (
    <Box
      sx={{
        mb: 3,
        bgcolor: 'var(--fx-paper)',
        border: '1px solid var(--fx-border)',
        borderRadius: 3,
        boxShadow: 'var(--fx-shadow-sm)',
        px: 1,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      {/* Left cluster: filter tabs + result count */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexWrap: 'wrap' }}>
        <FilterTabs tabs={tabs} value={filterValue} onChange={onFilterChange} />
        {countText && (
          <Typography
            sx={{ color: 'var(--fx-text-muted)', fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap', pl: 0.5 }}
          >
            {countText}
          </Typography>
        )}
      </Box>

      {/* Right cluster: search + sort + optional action */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {setSearchQuery && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--fx-text-muted)', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 230 },
              '& .MuiOutlinedInput-root': {
                height: 38,
                borderRadius: 2,
                bgcolor: 'var(--fx-subtle)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                '& fieldset': { borderColor: 'var(--fx-border)' },
                '&:hover fieldset': { borderColor: 'var(--fx-border-strong)' },
                '&.Mui-focused': { bgcolor: 'var(--fx-paper)', boxShadow: '0 0 0 3px rgba(0,135,180,0.12)' },
                '&.Mui-focused fieldset': { borderColor: '#0087B4', borderWidth: '1px' },
              },
              '& .MuiOutlinedInput-input': { fontSize: '0.875rem' },
            }}
          />
        )}

        {setSortBy && (
          <FormControl size="small" sx={{ minWidth: 148 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
              startAdornment={<SortIcon sx={{ color: 'var(--fx-text-muted)', mr: 0.75, ml: 0.25, fontSize: 16 }} />}
              sx={{
                height: 38,
                borderRadius: 2,
                bgcolor: 'var(--fx-subtle)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--fx-text-primary)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--fx-border)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--fx-border-strong)' },
                '&.Mui-focused': { bgcolor: 'var(--fx-paper)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0087B4', borderWidth: '1px' },
              }}
            >
              {sortOptions}
            </Select>
          </FormControl>
        )}

        {rightExtra && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5, ml: 0.5, borderLeft: '1px solid var(--fx-border)' }}>
            {rightExtra}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageToolbar;
